import Link from 'next/link'

import type { FC, ReactNode } from 'react'
import type { PlannerExercise } from '@/types'
import type { Experience, Goal, Plan, Setup, Sex, SoreSpot } from '@/lib/workout-planner'
import type { SavedInputs } from '@/lib/saved-workout-plan'

import { useEffect, useRef, useState } from 'react'

import {
    ArrowLeft,
    ArrowRight,
    CalendarDays,
    Check,
    ChevronRight,
    ClipboardList,
    Copy,
    Flame,
    Footprints,
    Info,
    LoaderCircle,
    Printer,
    RotateCcw,
    Smartphone,
    TriangleAlert
} from 'lucide-react'

import { QrCode } from '@/components/generals/qr-code'
import { cn } from '@/lib/utils'
import { buildPlan } from '@/lib/workout-planner'

import {
    PLANNER_PATH,
    PLAN_LINK_PARAM,
    clearSavedPlan,
    decodePlanLink,
    encodePlanLink,
    exerciseLink,
    plannerInput,
    readOpenedFrom,
    shareOrigin,
    readSavedPlan,
    readScroll,
    saveOpenedFrom,
    savePlan,
    saveScroll
} from '@/lib/saved-workout-plan'

// Health questions modelled on the factors ACSM uses for pre-exercise screening
// (known heart, metabolic or kidney disease, warning symptoms) and WHO's advice
// for pregnancy and chronic conditions. Our own wording, not a validated form.
const HEALTH_QUESTIONS = [
    'Has a doctor told you that you have a heart condition, or that you should only exercise with medical supervision?',
    'Do you get chest pain, chest pressure or unusual breathlessness, at rest or when you are active?',
    'Do you get dizzy or lose your balance, or have you fainted recently?',
    'Are you pregnant, or have you had a baby recently?',
    'Are you recovering from surgery or an injury, or do you live with a condition such as diabetes, kidney disease or joint problems?'
]

const EXPERIENCE_OPTIONS: { value: Experience; label: string; hint: string }[] = [
    { value: 'new', label: 'New or returning', hint: 'No regular strength training, or a long break' },
    { value: 'some', label: 'Some experience', hint: 'Training regularly for 6 months or more' },
    { value: 'regular', label: 'Experienced', hint: 'Training regularly for several years' }
]

const GOAL_OPTIONS: { value: Goal; label: string; hint: string }[] = [
    { value: 'health', label: 'Get fitter overall', hint: 'Balanced strength and cardio' },
    { value: 'strength', label: 'Build strength and muscle', hint: 'More focus on lifting' },
    { value: 'fat-loss', label: 'Support fat loss', hint: 'More weekly activity' },
    { value: 'endurance', label: 'Build stamina', hint: 'Shorter rests, more cardio' }
]

const SETUP_OPTIONS: { value: Setup; label: string; hint: string }[] = [
    { value: 'none', label: 'No equipment', hint: 'Bodyweight only' },
    { value: 'home', label: 'Home kit', hint: 'Dumbbells or bands' },
    { value: 'gym', label: 'Gym', hint: 'Barbells, machines, pull-up bar' }
]

const SORE_OPTIONS: { value: SoreSpot; label: string }[] = [
    { value: 'knees', label: 'Knees' },
    { value: 'back', label: 'Lower back' },
    { value: 'shoulders', label: 'Shoulders' }
]

const SEX_OPTIONS: { value: Sex; label: string }[] = [
    { value: 'female', label: 'Female' },
    { value: 'male', label: 'Male' },
    { value: 'unspecified', label: 'Prefer not to say' }
]

const DAY_OPTIONS = [2, 3, 4, 5]
const MINUTE_OPTIONS = [20, 30, 45, 60]
const STEPS = 3
const LB_PER_KG = 2.20462
const CM_PER_INCH = 2.54

// Shown one after another while the plan is "prepared". The plan itself is
// ready instantly; the pause lets people see what went into it.
const PREP_STEPS = [
    'Checking your answers',
    'Matching exercises to your equipment and experience',
    'Balancing your week so muscles get time to recover',
    'Adding cardio, progression and a calorie estimate'
]
const PREP_STEP_MS = 700

// The print frame's own rules, added after the page's stylesheets so they win.
// The page's print rules are written for printing the whole page; this frame
// holds nothing but the plan, so everything in it is shown and laid out plainly.
// Headings use the body sans because Fraunces distorts in browser PDFs.
const PRINT_FRAME_CSS = `
@page { margin: 14mm 12mm; }
html, body { margin: 0; background: #fff; }
body, body * { visibility: visible !important; }
[data-print-area] { position: static !important; inset: auto !important; padding: 0 !important; }
[data-print-area] .font-heading { font-family: var(--font-sans), ui-sans-serif, system-ui, sans-serif !important; letter-spacing: normal !important; }
[data-print-area] a { color: inherit !important; text-decoration: none !important; }
[data-print-area] [data-print-link] { text-decoration: underline !important; text-decoration-thickness: 1px !important; text-underline-offset: 2px !important; }
`

type Status = 'loading' | 'ready' | 'error'

const choiceClass = (selected: boolean): string =>
    cn(
        'flex flex-col items-start rounded-xl border px-4 py-3 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        selected
            ? 'border-primary bg-primary text-primary-foreground'
            : 'border-border bg-background text-foreground hover:bg-secondary'
    )

const chipClass = (selected: boolean): string =>
    cn(
        'rounded-full border px-4 py-2 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        selected
            ? 'border-primary bg-primary text-primary-foreground'
            : 'border-border bg-background text-foreground hover:bg-secondary'
    )

const Label: FC<{ children: ReactNode; htmlFor?: string; id?: string }> = ({ children, htmlFor, id }): ReactNode =>
    htmlFor ? (
        <label htmlFor={htmlFor} className='block text-sm font-semibold text-foreground'>{children}</label>
    ) : (
        <span id={id} className='block text-sm font-semibold text-foreground'>{children}</span>
    )

export const WorkoutPlanner: FC = (): ReactNode => {
    const [status, setStatus] = useState<Status>('loading')
    const [pool, setPool] = useState<PlannerExercise[]>([])

    const [step, setStep] = useState(1)
    const [answers, setAnswers] = useState<(boolean | null)[]>(HEALTH_QUESTIONS.map(() => null))
    const [age, setAge] = useState('')
    const [weight, setWeight] = useState('')
    const [unit, setUnit] = useState<'kg' | 'lb'>('kg')
    const [heightCm, setHeightCm] = useState('')
    const [feet, setFeet] = useState('')
    const [inches, setInches] = useState('')
    const [heightUnit, setHeightUnit] = useState<'cm' | 'ft'>('cm')
    const [sex, setSex] = useState<Sex>('unspecified')
    const [pending, setPending] = useState<Plan | null>(null)
    const [prepStep, setPrepStep] = useState(0)
    const [experience, setExperience] = useState<Experience>('new')
    const [goal, setGoal] = useState<Goal>('health')
    const [days, setDays] = useState(3)
    const [minutes, setMinutes] = useState(30)
    const [setup, setSetup] = useState<Setup>('none')
    const [sore, setSore] = useState<SoreSpot[]>([])
    const [plan, setPlan] = useState<Plan | null>(null)
    const [attempted, setAttempted] = useState(false)

    const headingRef = useRef<HTMLSpanElement>(null)
    const printAreaRef = useRef<HTMLDivElement>(null)
    const interacted = useRef(false)

    // Remove a leftover print frame if the planner leaves the page.
    useEffect(() => {
        return () => {
            document.querySelectorAll('iframe[data-plan-print-frame]').forEach((node) => node.remove())
        }
    }, [])

    // Prints the plan from its own clean document instead of the whole page, so
    // nothing around it (header, article, layout, preview tooling) can push it
    // off the page or hide it. Falls back to printing the page if that fails.
    const printPlan = (): void => {
        const area = printAreaRef.current
        if (!area) return

        document.querySelectorAll('iframe[data-plan-print-frame]').forEach((node) => node.remove())

        try {
            const frame = document.createElement('iframe')
            frame.setAttribute('data-plan-print-frame', '')
            frame.setAttribute('aria-hidden', 'true')
            frame.setAttribute('tabindex', '-1')
            frame.style.cssText = 'position:fixed;top:0;left:-10000px;width:900px;height:1200px;border:0;'
            document.body.appendChild(frame)

            const frameWindow = frame.contentWindow
            const frameDocument = frame.contentDocument
            if (!frameWindow || !frameDocument) {
                frame.remove()
                window.print()
                return
            }

            frameDocument.open()
            frameDocument.write('<!doctype html><html lang="en"><head><meta charset="utf-8"></head><body></body></html>')
            frameDocument.close()
            frameDocument.title = 'Personalized Workout Plan'

            const base = frameDocument.createElement('base')
            base.href = `${window.location.origin}/`
            frameDocument.head.appendChild(base)

            document.querySelectorAll('link[rel="stylesheet"], style').forEach((node) => {
                frameDocument.head.appendChild(frameDocument.importNode(node, true))
            })
            const printStyle = frameDocument.createElement('style')
            printStyle.textContent = PRINT_FRAME_CSS
            frameDocument.head.appendChild(printStyle)
            frameDocument.documentElement.className = document.documentElement.className

            // The font variables live on the app wrapper, so carry their values across.
            const wrapper = frameDocument.createElement('div')
            wrapper.className = 'bg-background font-sans text-foreground antialiased'
            const computed = window.getComputedStyle(area)
            for (const name of ['--font-sans', '--font-heading']) {
                const value = computed.getPropertyValue(name)
                if (value) wrapper.style.setProperty(name, value)
            }
            wrapper.appendChild(frameDocument.importNode(area, true))
            frameDocument.body.appendChild(wrapper)

            // In the PDF, exercise links carry the plan, so tapping one on any
            // device opens the exercise with a way back to this same plan.
            frameDocument.querySelectorAll<HTMLAnchorElement>('a[data-print-href]').forEach((link) => {
                link.href = link.dataset.printHref ?? link.href
            })

            const sheets = Array.from(frameDocument.querySelectorAll<HTMLLinkElement>('link[rel="stylesheet"]'))
            const sheetsLoaded = Promise.all(sheets.map((link) => new Promise<void>((resolve) => {
                link.addEventListener('load', () => resolve())
                link.addEventListener('error', () => resolve())
            })))
            const timeout = new Promise<void>((resolve) => window.setTimeout(resolve, 3000))

            frameWindow.addEventListener('afterprint', () => {
                window.setTimeout(() => frame.remove(), 500)
            })

            Promise.race([sheetsLoaded, timeout])
                .then(() => {
                    // Lay the page out so its fonts start loading, then wait for them.
                    void frameDocument.body.offsetHeight
                    return Promise.race([frameDocument.fonts.ready.then(() => undefined), timeout])
                })
                .then(() => {
                    frameWindow.focus()
                    frameWindow.print()
                })
                .catch(() => {
                    frame.remove()
                    window.print()
                })
        } catch {
            window.print()
        }
    }

    const loadExercises = () => {
        setStatus('loading')
        fetch('/api/exercises')
            .then((response) => response.json())
            .then((data: { ok: boolean; exercises?: PlannerExercise[] }) => {
                if (data.ok && data.exercises) {
                    setPool(data.exercises)
                    setStatus('ready')
                } else {
                    setStatus('error')
                }
            })
            .catch(() => setStatus('error'))
    }

    useEffect(() => {
        loadExercises()
    }, [])

    // The finished plan is remembered on this device (see @/lib/saved-workout-plan),
    // so coming back from an exercise page, or another day, shows the plan again
    // instead of a blank quiz. A plan link (?plan=...) rebuilds the same plan on
    // any device. Restored after mount, so server and first client render match.
    const restoring = useRef(false)
    const linkInputs = useRef<SavedInputs | null>(null)
    // The exercise last opened from the plan, highlighted when the visitor returns.
    const [lastViewed, setLastViewed] = useState<{ day: string; slug: string } | null>(null)
    const [planLink, setPlanLink] = useState<string | null>(null)
    const [origin, setOrigin] = useState('')
    const [copied, setCopied] = useState(false)
    // A plan link is being opened: the plan appears once the exercises load.
    const [opening, setOpening] = useState(false)

    const applyInputs = (inputs: SavedInputs): void => {
        // A plan link keeps one health answer; pad to the full list of questions.
        setAnswers(HEALTH_QUESTIONS.map((_, index) => inputs.answers[index] ?? false))
        setAge(inputs.age)
        setWeight(inputs.weight)
        setUnit(inputs.unit)
        setHeightCm(inputs.heightCm)
        setFeet(inputs.feet)
        setInches(inputs.inches)
        setHeightUnit(inputs.heightUnit)
        setSex(inputs.sex)
        setExperience(inputs.experience)
        setGoal(inputs.goal)
        setDays(DAY_OPTIONS.includes(inputs.days) ? inputs.days : 3)
        setMinutes(MINUTE_OPTIONS.includes(inputs.minutes) ? inputs.minutes : 30)
        setSetup(inputs.setup)
        setSore(inputs.sore)
        setStep(3)
    }

    useEffect(() => {
        setOrigin(shareOrigin())

        const linked = new URLSearchParams(window.location.search).get(PLAN_LINK_PARAM)
        const fromLink = linked ? decodePlanLink(linked, HEALTH_QUESTIONS.length) : null
        const saved = readSavedPlan()

        // The saved plan is the same one the link describes: restore it as is.
        if (saved && (!fromLink || encodePlanLink(saved.inputs) === linked)) {
            applyInputs(saved.inputs)
            setLastViewed(readOpenedFrom())
            restoring.current = true
            setPlan(saved.plan)
            return
        }

        // A different plan from a link wins: it is what the visitor just opened.
        // It is rebuilt once the exercise library has loaded.
        if (fromLink) {
            applyInputs(fromLink)
            linkInputs.current = fromLink
            setOpening(true)
        }
    }, [])

    // Rebuild a linked plan as soon as the exercise library is in.
    useEffect(() => {
        const inputs = linkInputs.current
        if (status !== 'ready' || !inputs) return
        linkInputs.current = null
        setOpening(false)
        setPlan(buildPlan(pool, plannerInput(inputs)))
    }, [status])

    useEffect(() => {
        if (plan) {
            const inputs: SavedInputs = { answers, age, weight, unit, heightCm, feet, inches, heightUnit, sex, experience, goal, days, minutes, setup, sore }
            savePlan(plan, inputs)
            setPlanLink(`${shareOrigin()}${PLANNER_PATH}?${PLAN_LINK_PARAM}=${encodePlanLink(inputs)}`)
        } else {
            setPlanLink(null)
            if (interacted.current) {
                clearSavedPlan()
                setLastViewed(null)
                // Drop an old plan link from the address bar, so a reload does not bring it back.
                const url = new URL(window.location.href)
                if (url.searchParams.has(PLAN_LINK_PARAM)) {
                    url.searchParams.delete(PLAN_LINK_PARAM)
                    window.history.replaceState(window.history.state, '', url.pathname + url.search + url.hash)
                }
            }
        }
        setCopied(false)

        // After a restore, bring the last opened exercise back into view, or the
        // saved scroll position if that row is not on the page.
        if (plan && restoring.current) {
            restoring.current = false
            const opened = readOpenedFrom()
            const scroll = readScroll()
            window.requestAnimationFrame(() => {
                const row = opened ? document.getElementById(`plan-exercise-${opened.day}-${opened.slug}`) : null
                if (row) row.scrollIntoView({ block: 'center' })
                else if (scroll !== null) window.scrollTo({ top: scroll })
            })
        }
    }, [plan])

    const openExercise = (day: string, slug: string): void => {
        saveScroll()
        saveOpenedFrom({ day, slug })
    }

    const copyPlanLink = (): void => {
        if (!planLink) return
        const done = () => {
            setCopied(true)
            window.setTimeout(() => setCopied(false), 2500)
        }
        if (navigator.clipboard?.writeText) {
            navigator.clipboard.writeText(planLink).then(done).catch(() => window.prompt('Copy your plan link:', planLink))
        } else {
            window.prompt('Copy your plan link:', planLink)
        }
    }

    useEffect(() => {
        if (interacted.current) headingRef.current?.focus()
    }, [step, plan])

    // Step through the "preparing" messages, then reveal the plan.
    useEffect(() => {
        if (!pending) return

        if (prepStep >= PREP_STEPS.length) {
            setPlan(pending)
            setPending(null)
            return
        }

        const timer = window.setTimeout(() => setPrepStep((value) => value + 1), PREP_STEP_MS)
        return () => window.clearTimeout(timer)
    }, [pending, prepStep])

    const cautious = answers.some((answer) => answer === true)
    const healthDone = answers.every((answer) => answer !== null)

    const ageValue = Number(age)
    const weightValue = Number(weight)
    const weightKg = unit === 'kg' ? weightValue : weightValue / LB_PER_KG
    const ageValid = age !== '' && ageValue >= 18 && ageValue <= 100
    const underage = age !== '' && ageValue > 0 && ageValue < 18
    const weightValid = weight !== '' && weightKg >= 30 && weightKg <= 300
    const heightValue = heightUnit === 'cm'
        ? Number(heightCm)
        : (Number(feet) * 12 + Number(inches || 0)) * CM_PER_INCH
    const heightValid = (heightUnit === 'cm' ? heightCm !== '' : feet !== '') && heightValue >= 120 && heightValue <= 230

    const go = (next: number) => {
        interacted.current = true
        setAttempted(false)
        setStep(next)
    }

    const answer = (index: number, value: boolean) => {
        setAnswers((current) => current.map((item, i) => (i === index ? value : item)))
    }

    const toggleSore = (value: SoreSpot) => {
        setSore((current) => (current.includes(value) ? current.filter((item) => item !== value) : [...current, value]))
    }

    const submit = () => {
        interacted.current = true
        if (status !== 'ready') return
        setPrepStep(0)
        setPending(
            buildPlan(pool, {
                age: ageValue,
                weightKg,
                heightCm: heightValue,
                sex,
                experience,
                goal,
                days,
                minutes,
                setup,
                sore,
                cautious
            })
        )
    }

    const restart = () => {
        interacted.current = true
        setPlan(null)
        setAnswers(HEALTH_QUESTIONS.map(() => null))
        setAge('')
        setWeight('')
        setHeightCm('')
        setFeet('')
        setInches('')
        setSex('unspecified')
        setSore([])
        setStep(1)
    }

    const perDay = plan && plan.cardioShortfall > 0 ? Math.ceil(plan.cardioShortfall / 5 / 5) * 5 : 0
    const waitingForLink = opening && status !== 'error'
    const showForm = !plan && !pending && !waitingForLink
    const currentInputs: SavedInputs = { answers, age, weight, unit, heightCm, feet, inches, heightUnit, sex, experience, goal, days, minutes, setup, sore }

    return (
        <section
            aria-label='Workout plan builder'
            className='my-10 overflow-hidden rounded-2xl border border-border bg-card shadow-sm print:border-0 print:shadow-none'
        >
            <div className='border-b border-border bg-secondary/50 px-5 py-4 sm:px-6'>
                <div className='flex items-center gap-2'>
                    <ClipboardList className='size-5 shrink-0 text-primary' />
                    <span className='font-heading text-lg font-semibold tracking-tight text-foreground'>
                        Workout plan builder
                    </span>
                </div>
                <span className='mt-1 block text-sm text-muted-foreground'>
                    Answer a few questions and get a 4-week plan built from our exercise library.
                </span>
                {showForm && (
                    <span className='mt-3 flex flex-wrap gap-2'>
                        <span className='rounded-full bg-background px-3 py-1 text-xs font-medium text-foreground'>{STEPS} short steps</span>
                        <span className='rounded-full bg-background px-3 py-1 text-xs font-medium text-foreground'>About 2 minutes</span>
                        <span className='rounded-full bg-background px-3 py-1 text-xs font-medium text-foreground'>Stays in your browser</span>
                    </span>
                )}
            </div>

            <div className='px-5 py-6 sm:px-6'>
                {showForm && (
                    <div>
                        <span className='text-sm font-semibold text-primary'>Step {step} of {STEPS}</span>
                        <div
                            className='mt-3 grid grid-cols-3 gap-1.5'
                            role='progressbar'
                            aria-valuemin={1}
                            aria-valuemax={STEPS}
                            aria-valuenow={step}
                            aria-valuetext={`Step ${step} of ${STEPS}`}
                            aria-label='Planner progress'
                        >
                            {[1, 2, 3].map((item) => (
                                <span
                                    key={item}
                                    className={cn('h-2 rounded-full', item < step ? 'bg-primary' : item === step ? 'bg-primary/40' : 'bg-secondary')}
                                />
                            ))}
                        </div>
                    </div>
                )}

                {showForm && step === 1 && (
                    <div className='mt-6'>
                        <span ref={headingRef} tabIndex={-1} className='block font-heading text-xl font-semibold tracking-tight text-foreground outline-none'>
                            Quick health check
                        </span>
                        <span className='mt-1 block text-sm text-muted-foreground'>
                            These questions help decide whether to check with a doctor first.
                        </span>

                        <span className='mt-5 flex flex-col gap-4'>
                            {HEALTH_QUESTIONS.map((question, index) => (
                                <span key={question} className='block rounded-xl border border-border bg-background px-4 py-3'>
                                    <span id={`health-${index}`} className='block text-sm leading-relaxed text-foreground'>{question}</span>
                                    <span role='group' aria-labelledby={`health-${index}`} className='mt-3 flex gap-2'>
                                        {[true, false].map((value) => (
                                            <button
                                                key={String(value)}
                                                type='button'
                                                aria-pressed={answers[index] === value}
                                                onClick={() => answer(index, value)}
                                                className={chipClass(answers[index] === value)}
                                            >
                                                {value ? 'Yes' : 'No'}
                                            </button>
                                        ))}
                                    </span>
                                </span>
                            ))}
                        </span>

                        {cautious && (
                            <span className='mt-5 flex gap-3 rounded-xl border border-primary/40 bg-primary/5 px-4 py-4 text-sm leading-relaxed text-foreground'>
                                <TriangleAlert className='mt-0.5 size-5 shrink-0 text-primary' />
                                <span>
                                    Please speak to a doctor before starting a new exercise plan. You can still build a gentle
                                    starter plan with short sessions to take to that conversation.
                                </span>
                            </span>
                        )}

                        {attempted && !healthDone && (
                            <span role='alert' className='mt-4 block text-sm font-medium text-destructive'>
                                Please answer all {HEALTH_QUESTIONS.length} questions.
                            </span>
                        )}

                        <span className='mt-6 flex justify-end'>
                            <button
                                type='button'
                                onClick={() => (healthDone ? go(2) : setAttempted(true))}
                                className='inline-flex items-center gap-2 rounded-full bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground transition-opacity hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2'
                            >
                                {cautious ? 'Build a gentle plan' : 'Next: about you'}
                                <ArrowRight className='size-4' />
                            </button>
                        </span>
                    </div>
                )}

                {showForm && step === 2 && (
                    <div className='mt-6'>
                        <span ref={headingRef} tabIndex={-1} className='block font-heading text-xl font-semibold tracking-tight text-foreground outline-none'>
                            About you
                        </span>
                        <span className='mt-1 block text-sm text-muted-foreground'>
                            Age shapes the plan. Height, weight and sex are only used to estimate calories.
                        </span>

                        <span className='mt-5 grid gap-4 sm:grid-cols-2'>
                            <span className='block'>
                                <Label htmlFor='planner-age'>Age</Label>
                                <input
                                    id='planner-age'
                                    inputMode='numeric'
                                    pattern='[0-9]*'
                                    autoComplete='off'
                                    value={age}
                                    onChange={(event) => setAge(event.target.value.replace(/[^0-9]/g, '').slice(0, 3))}
                                    aria-invalid={attempted && !ageValid}
                                    className='mt-2 h-12 w-full rounded-xl border border-border bg-background px-4 text-base text-foreground outline-none focus-visible:ring-2 focus-visible:ring-ring'
                                    placeholder='e.g. 42'
                                />
                            </span>
                            <span className='block'>
                                <Label htmlFor='planner-weight'>Weight</Label>
                                <span className='mt-2 flex gap-2'>
                                    <input
                                        id='planner-weight'
                                        inputMode='decimal'
                                        autoComplete='off'
                                        value={weight}
                                        onChange={(event) => setWeight(event.target.value.replace(/[^0-9.]/g, '').slice(0, 5))}
                                        aria-invalid={attempted && !weightValid}
                                        className='h-12 w-full min-w-0 rounded-xl border border-border bg-background px-4 text-base text-foreground outline-none focus-visible:ring-2 focus-visible:ring-ring'
                                        placeholder={unit === 'kg' ? 'e.g. 70' : 'e.g. 155'}
                                    />
                                    <span role='group' aria-label='Weight unit' className='flex shrink-0 overflow-hidden rounded-xl border border-border'>
                                        {(['kg', 'lb'] as const).map((value) => (
                                            <button
                                                key={value}
                                                type='button'
                                                aria-pressed={unit === value}
                                                onClick={() => setUnit(value)}
                                                className={cn(
                                                    'px-3 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring',
                                                    unit === value ? 'bg-primary text-primary-foreground' : 'bg-background text-foreground hover:bg-secondary'
                                                )}
                                            >
                                                {value}
                                            </button>
                                        ))}
                                    </span>
                                </span>
                            </span>
                            <span className='block'>
                                <Label htmlFor={heightUnit === 'cm' ? 'planner-height' : 'planner-feet'}>Height</Label>
                                <span className='mt-2 flex gap-2'>
                                    {heightUnit === 'cm' ? (
                                        <input
                                            id='planner-height'
                                            inputMode='numeric'
                                            pattern='[0-9]*'
                                            autoComplete='off'
                                            value={heightCm}
                                            onChange={(event) => setHeightCm(event.target.value.replace(/[^0-9]/g, '').slice(0, 3))}
                                            aria-invalid={attempted && !heightValid}
                                            className='h-12 w-full min-w-0 rounded-xl border border-border bg-background px-4 text-base text-foreground outline-none focus-visible:ring-2 focus-visible:ring-ring'
                                            placeholder='e.g. 170'
                                        />
                                    ) : (
                                        <span className='flex w-full min-w-0 gap-2'>
                                            <input
                                                id='planner-feet'
                                                inputMode='numeric'
                                                pattern='[0-9]*'
                                                autoComplete='off'
                                                aria-label='Feet'
                                                value={feet}
                                                onChange={(event) => setFeet(event.target.value.replace(/[^0-9]/g, '').slice(0, 1))}
                                                aria-invalid={attempted && !heightValid}
                                                className='h-12 w-full min-w-0 rounded-xl border border-border bg-background px-4 text-base text-foreground outline-none focus-visible:ring-2 focus-visible:ring-ring'
                                                placeholder='ft'
                                            />
                                            <input
                                                inputMode='numeric'
                                                pattern='[0-9]*'
                                                autoComplete='off'
                                                aria-label='Inches'
                                                value={inches}
                                                onChange={(event) => setInches(event.target.value.replace(/[^0-9]/g, '').slice(0, 2))}
                                                className='h-12 w-full min-w-0 rounded-xl border border-border bg-background px-4 text-base text-foreground outline-none focus-visible:ring-2 focus-visible:ring-ring'
                                                placeholder='in'
                                            />
                                        </span>
                                    )}
                                    <span role='group' aria-label='Height unit' className='flex shrink-0 overflow-hidden rounded-xl border border-border'>
                                        {(['cm', 'ft'] as const).map((value) => (
                                            <button
                                                key={value}
                                                type='button'
                                                aria-pressed={heightUnit === value}
                                                onClick={() => setHeightUnit(value)}
                                                className={cn(
                                                    'px-3 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring',
                                                    heightUnit === value ? 'bg-primary text-primary-foreground' : 'bg-background text-foreground hover:bg-secondary'
                                                )}
                                            >
                                                {value === 'cm' ? 'cm' : 'ft/in'}
                                            </button>
                                        ))}
                                    </span>
                                </span>
                            </span>
                            <span className='block'>
                                <Label id='planner-sex'>Sex</Label>
                                <span role='group' aria-labelledby='planner-sex' className='mt-2 flex flex-wrap gap-2'>
                                    {SEX_OPTIONS.map((option) => (
                                        <button
                                            key={option.value}
                                            type='button'
                                            aria-pressed={sex === option.value}
                                            onClick={() => setSex(option.value)}
                                            className={chipClass(sex === option.value)}
                                        >
                                            {option.label}
                                        </button>
                                    ))}
                                </span>
                            </span>
                        </span>

                        {underage && (
                            <span role='alert' className='mt-4 block rounded-xl border border-border bg-secondary/40 px-4 py-3 text-sm text-foreground'>
                                This planner is built for adults aged 18 and over. Children and teenagers have different activity
                                guidelines, so it will not build a plan for this age.
                            </span>
                        )}
                        {attempted && !underage && (!ageValid || !weightValid || !heightValid) && (
                            <span role='alert' className='mt-4 block text-sm font-medium text-destructive'>
                                {!ageValid ? 'Enter an age between 18 and 100. ' : ''}
                                {!weightValid ? 'Enter your weight. ' : ''}
                                {!heightValid ? 'Enter your height.' : ''}
                            </span>
                        )}

                        <span className='mt-6 block'>
                            <Label id='planner-experience'>Strength training experience</Label>
                            <span role='group' aria-labelledby='planner-experience' className='mt-2 grid gap-2 sm:grid-cols-3'>
                                {EXPERIENCE_OPTIONS.map((option) => (
                                    <button
                                        key={option.value}
                                        type='button'
                                        aria-pressed={experience === option.value}
                                        onClick={() => setExperience(option.value)}
                                        className={choiceClass(experience === option.value)}
                                    >
                                        <span className='text-sm font-semibold'>{option.label}</span>
                                        <span className={cn('mt-0.5 text-xs', experience === option.value ? 'text-primary-foreground/80' : 'text-muted-foreground')}>
                                            {option.hint}
                                        </span>
                                    </button>
                                ))}
                            </span>
                        </span>

                        <span className='mt-6 flex justify-between gap-3'>
                            <button
                                type='button'
                                onClick={() => go(1)}
                                className='inline-flex items-center gap-2 rounded-full border border-border bg-background px-5 py-3 text-sm font-medium text-foreground transition-colors hover:bg-secondary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
                            >
                                <ArrowLeft className='size-4' />
                                Back
                            </button>
                            <button
                                type='button'
                                onClick={() => (ageValid && weightValid && heightValid ? go(3) : setAttempted(true))}
                                disabled={underage}
                                className='inline-flex items-center gap-2 rounded-full bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground transition-opacity hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:opacity-50'
                            >
                                Next: your week
                                <ArrowRight className='size-4' />
                            </button>
                        </span>
                    </div>
                )}

                {showForm && step === 3 && (
                    <div className='mt-6'>
                        <span ref={headingRef} tabIndex={-1} className='block font-heading text-xl font-semibold tracking-tight text-foreground outline-none'>
                            Your week
                        </span>

                        <span className='mt-5 block'>
                            <Label id='planner-goal'>Main goal</Label>
                            <span role='group' aria-labelledby='planner-goal' className='mt-2 grid gap-2 sm:grid-cols-2'>
                                {GOAL_OPTIONS.map((option) => (
                                    <button
                                        key={option.value}
                                        type='button'
                                        aria-pressed={goal === option.value}
                                        onClick={() => setGoal(option.value)}
                                        className={choiceClass(goal === option.value)}
                                    >
                                        <span className='text-sm font-semibold'>{option.label}</span>
                                        <span className={cn('mt-0.5 text-xs', goal === option.value ? 'text-primary-foreground/80' : 'text-muted-foreground')}>
                                            {option.hint}
                                        </span>
                                    </button>
                                ))}
                            </span>
                        </span>

                        <span className='mt-6 grid gap-6 sm:grid-cols-2'>
                            <span className='block'>
                                <Label id='planner-days'>Days a week you can train</Label>
                                <span role='group' aria-labelledby='planner-days' className='mt-2 flex flex-wrap gap-2'>
                                    {DAY_OPTIONS.map((value) => (
                                        <button key={value} type='button' aria-pressed={days === value} onClick={() => setDays(value)} className={chipClass(days === value)}>
                                            {value} days
                                        </button>
                                    ))}
                                </span>
                            </span>
                            <span className='block'>
                                <Label id='planner-minutes'>Time per session</Label>
                                <span role='group' aria-labelledby='planner-minutes' className='mt-2 flex flex-wrap gap-2'>
                                    {MINUTE_OPTIONS.map((value) => (
                                        <button key={value} type='button' aria-pressed={minutes === value} onClick={() => setMinutes(value)} className={chipClass(minutes === value)}>
                                            {value} min
                                        </button>
                                    ))}
                                </span>
                            </span>
                        </span>

                        <span className='mt-6 block'>
                            <Label id='planner-setup'>Equipment</Label>
                            <span role='group' aria-labelledby='planner-setup' className='mt-2 grid gap-2 sm:grid-cols-3'>
                                {SETUP_OPTIONS.map((option) => (
                                    <button
                                        key={option.value}
                                        type='button'
                                        aria-pressed={setup === option.value}
                                        onClick={() => setSetup(option.value)}
                                        className={choiceClass(setup === option.value)}
                                    >
                                        <span className='text-sm font-semibold'>{option.label}</span>
                                        <span className={cn('mt-0.5 text-xs', setup === option.value ? 'text-primary-foreground/80' : 'text-muted-foreground')}>
                                            {option.hint}
                                        </span>
                                    </button>
                                ))}
                            </span>
                        </span>

                        <span className='mt-6 block'>
                            <Label id='planner-sore'>Anything sore to work around? (optional)</Label>
                            <span role='group' aria-labelledby='planner-sore' className='mt-2 flex flex-wrap gap-2'>
                                {SORE_OPTIONS.map((option) => (
                                    <button
                                        key={option.value}
                                        type='button'
                                        aria-pressed={sore.includes(option.value)}
                                        onClick={() => toggleSore(option.value)}
                                        className={chipClass(sore.includes(option.value))}
                                    >
                                        {option.label}
                                    </button>
                                ))}
                            </span>
                        </span>

                        {status === 'error' && (
                            <span role='alert' className='mt-5 flex flex-wrap items-center gap-3 rounded-xl border border-border bg-secondary/40 px-4 py-3 text-sm text-foreground'>
                                The exercise list did not load.
                                <button type='button' onClick={loadExercises} className='font-medium text-primary underline underline-offset-2'>
                                    Try again
                                </button>
                            </span>
                        )}

                        <span className='mt-6 flex justify-between gap-3'>
                            <button
                                type='button'
                                onClick={() => go(2)}
                                className='inline-flex items-center gap-2 rounded-full border border-border bg-background px-5 py-3 text-sm font-medium text-foreground transition-colors hover:bg-secondary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
                            >
                                <ArrowLeft className='size-4' />
                                Back
                            </button>
                            <button
                                type='button'
                                onClick={submit}
                                disabled={status !== 'ready'}
                                className='inline-flex items-center gap-2 rounded-full bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground transition-opacity hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:opacity-50'
                            >
                                {status === 'loading' ? 'Loading exercises...' : 'Build my plan'}
                                <Check className='size-4' />
                            </button>
                        </span>
                    </div>
                )}

                {waitingForLink && !plan && (
                    <div role='status' aria-live='polite' className='flex flex-col items-center py-10 text-center'>
                        <span className='flex size-14 items-center justify-center rounded-full bg-primary/10'>
                            <LoaderCircle className='size-6 text-primary motion-safe:animate-spin' />
                        </span>
                        <span className='mt-4 block font-heading text-xl font-semibold tracking-tight text-foreground'>
                            Opening your plan
                        </span>
                    </div>
                )}

                {pending && (
                    <div role='status' aria-live='polite' className='flex flex-col items-center py-8 text-center'>
                        <span className='flex size-14 items-center justify-center rounded-full bg-primary/10'>
                            <LoaderCircle className='size-6 text-primary motion-safe:animate-spin' />
                        </span>
                        <span className='mt-4 block font-heading text-xl font-semibold tracking-tight text-foreground'>
                            Preparing your plan
                        </span>
                        <span className='mt-6 flex w-full max-w-sm flex-col gap-3 text-left'>
                            {PREP_STEPS.map((label, index) => {
                                const done = index < prepStep
                                const active = index === prepStep
                                return (
                                    <span
                                        key={label}
                                        className={cn(
                                            'flex items-center gap-3 text-sm transition-opacity duration-300',
                                            done || active ? 'opacity-100' : 'opacity-40'
                                        )}
                                    >
                                        <span
                                            className={cn(
                                                'flex size-6 shrink-0 items-center justify-center rounded-full border',
                                                done ? 'border-primary bg-primary text-primary-foreground' : 'border-border bg-background'
                                            )}
                                        >
                                            {done ? (
                                                <Check className='size-4' />
                                            ) : active ? (
                                                <LoaderCircle className='size-4 text-primary motion-safe:animate-spin' />
                                            ) : null}
                                        </span>
                                        <span className={cn(done || active ? 'text-foreground' : 'text-muted-foreground')}>{label}</span>
                                    </span>
                                )
                            })}
                        </span>
                    </div>
                )}

                {plan && (
                    <div data-print-area ref={printAreaRef}>
                        <span className='mb-6 hidden items-center gap-5 rounded-xl border border-border px-4 py-4 print:flex'>
                            <span className='min-w-0 flex-1'>
                                <span className='block text-sm font-semibold text-foreground'>
                                    Made with the free workout plan builder from WellPath Fusion
                                </span>
                                <span className='mt-2 block text-sm leading-relaxed text-foreground'>
                                    Every exercise name below is a link: tap it to see how to do it, step by step.
                                </span>
                                <span className='mt-1 block text-sm leading-relaxed text-muted-foreground'>
                                    On paper? Scan the code with your phone camera to open this plan with all its links.
                                    {planLink && (
                                        <>
                                            {' '}Or{' '}
                                            <a href={planLink} className='font-medium text-foreground'>
                                                <span data-print-link>open your plan online</span>
                                            </a>.
                                        </>
                                    )}
                                </span>
                            </span>
                            {planLink && (
                                <QrCode value={planLink} label='Scan to open this plan' className='size-28 shrink-0' />
                            )}
                        </span>
                        <span ref={headingRef} tabIndex={-1} className='block font-heading text-2xl font-semibold tracking-tight text-foreground outline-none'>
                            Your 4-week plan
                        </span>

                        <span className='mt-4 grid gap-2 sm:grid-cols-3'>
                            <span className='flex items-center gap-2 rounded-xl bg-secondary/50 px-3 py-2.5 text-sm text-foreground'>
                                <CalendarDays className='size-4 shrink-0 text-primary' />
                                {plan.strengthDays} strength days a week
                            </span>
                            <span className='flex items-center gap-2 rounded-xl bg-secondary/50 px-3 py-2.5 text-sm text-foreground'>
                                <ClipboardList className='size-4 shrink-0 text-primary' />
                                {plan.sets} sets of {plan.reps} reps
                            </span>
                            <span className='flex items-center gap-2 rounded-xl bg-secondary/50 px-3 py-2.5 text-sm text-foreground'>
                                <Footprints className='size-4 shrink-0 text-primary' />
                                Rest {plan.rest}
                            </span>
                        </span>

                        {plan.notes.length > 0 && (
                            <span className='mt-5 flex flex-col gap-2 rounded-xl border border-primary/40 bg-primary/5 px-4 py-4'>
                                {plan.notes.map((note) => (
                                    <span key={note} className='flex gap-2 text-sm leading-relaxed text-foreground'>
                                        <TriangleAlert className='mt-0.5 size-4 shrink-0 text-primary' />
                                        {note}
                                    </span>
                                ))}
                            </span>
                        )}

                        <span className='mt-6 block text-sm font-semibold text-foreground'>Your week</span>
                        <span className='mt-3 grid gap-3 sm:grid-cols-2'>
                            {plan.days.filter((entry) => entry.session).map((entry) => {
                                const session = entry.session!
                                return (
                                    <span key={entry.day} className='block break-inside-avoid rounded-xl border border-border bg-background px-4 py-4'>
                                        <span className='flex items-baseline justify-between gap-3'>
                                            <span className='font-heading text-base font-semibold text-foreground'>
                                                {entry.day}: {session.title}
                                            </span>
                                            <span className='shrink-0 text-xs text-muted-foreground'>{session.minutes} min</span>
                                        </span>

                                        {session.kind === 'strength' ? (
                                            <span className='mt-3 flex flex-col gap-1.5'>
                                                <span className='text-xs text-muted-foreground'>
                                                    Warm up with a few minutes of easy movement, then tap an exercise to see how to do it:
                                                </span>
                                                {session.exercises.map((exercise, index) => {
                                                    const viewed = lastViewed?.day === entry.day && lastViewed.slug === exercise.slug

                                                    return (
                                                        <Link
                                                            key={exercise.slug}
                                                            id={`plan-exercise-${entry.day}-${exercise.slug}`}
                                                            href={`/exercises/${exercise.muscle}/${exercise.slug}`}
                                                            data-print-href={origin ? exerciseLink(origin, exercise, entry.day, currentInputs) : undefined}
                                                            onClick={() => openExercise(entry.day, exercise.slug)}
                                                            className={cn(
                                                                'group flex min-h-11 items-center gap-3 rounded-lg border px-3 py-2 text-sm no-underline transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring print:min-h-0 print:border-0 print:px-0 print:py-0.5',
                                                                viewed
                                                                    ? 'border-primary bg-primary/10'
                                                                    : 'border-border bg-card hover:border-primary/50 hover:bg-secondary/60'
                                                            )}
                                                        >
                                                            <span className='flex size-6 shrink-0 items-center justify-center rounded-full bg-secondary text-xs font-semibold tabular-nums text-foreground print:size-auto print:bg-transparent'>
                                                                {index + 1}
                                                            </span>
                                                            <span className='min-w-0 flex-1 font-medium text-foreground'>
                                                                <span data-print-link data-mdlfy-cms={`exercises:${exercise.id}:name`}>{exercise.name}</span>
                                                                {viewed && (
                                                                    <span className='mt-0.5 block text-xs font-normal text-primary print:hidden'>Last viewed</span>
                                                                )}
                                                            </span>
                                                            <span className='flex shrink-0 items-center gap-1 text-xs font-medium text-primary print:hidden'>
                                                                <span className='hidden sm:inline'>How to do it</span>
                                                                <ChevronRight className='size-4 transition-transform group-hover:translate-x-0.5' />
                                                            </span>
                                                        </Link>
                                                    )
                                                })}
                                            </span>
                                        ) : (
                                            <span className='mt-3 block text-sm leading-relaxed text-muted-foreground'>
                                                Brisk walking, cycling, swimming or any activity that makes you breathe harder but still lets you talk.
                                            </span>
                                        )}

                                        {session.balance && (
                                            <span className='mt-3 block rounded-lg bg-secondary/60 px-3 py-2 text-xs leading-relaxed text-foreground'>
                                                Add balance practice: stand on one leg beside a counter you can hold, then try walking heel to toe.
                                            </span>
                                        )}
                                    </span>
                                )
                            })}
                        </span>
                        <span className='mt-2 block text-xs text-muted-foreground'>
                            Other days are rest days. Days can move, but keep a day between sessions that train the same muscles.
                        </span>

                        <span className='mt-6 flex gap-3 rounded-xl border border-border bg-secondary/40 px-4 py-4 text-sm leading-relaxed text-foreground'>
                            <Footprints className='mt-0.5 size-5 shrink-0 text-primary' />
                            <span>
                                {plan.cardioShortfall > 0 ? (
                                    <>
                                        To reach {plan.cardioTarget} minutes of moderate activity a week, the target WHO sets for
                                        adults, add about {plan.cardioShortfall} more minutes of brisk walking or similar, for
                                        example {perDay} minutes on 5 days. Vigorous activity counts about double.
                                    </>
                                ) : (
                                    <>This plan already includes {plan.cardioPlanned} minutes of cardio, meeting the {plan.cardioTarget}-minute weekly target from WHO.</>
                                )}
                            </span>
                        </span>

                        <span className='mt-6 block text-sm font-semibold text-foreground'>How to progress</span>
                        <span className='mt-3 grid gap-2 sm:grid-cols-2'>
                            {plan.weeks.map((week) => (
                                <span key={week.title} className='block break-inside-avoid rounded-xl border border-border bg-background px-4 py-3'>
                                    <span className='block text-sm font-semibold text-foreground'>{week.title}</span>
                                    <span className='mt-1 block text-sm leading-relaxed text-muted-foreground'>{week.text}</span>
                                </span>
                            ))}
                        </span>

                        <span className='mt-6 flex gap-3 rounded-xl border border-border bg-background px-4 py-4 text-sm leading-relaxed text-foreground'>
                            <Flame className='mt-0.5 size-5 shrink-0 text-primary' />
                            <span>
                                These sessions, plus the extra walking, burn roughly{' '}
                                <span className='font-semibold'>{plan.calories[0].toLocaleString('en-US')} to {plan.calories[1].toLocaleString('en-US')} calories a week</span>{' '}
                                for your height, weight, age and sex. It is an estimate from average values, and real numbers vary from person to person.
                                {ageValue >= 60 && ' The values come from studies of adults under 60, so the estimate may run high for you.'}
                            </span>
                        </span>

                        <span className='mt-6 hidden text-xs leading-relaxed text-muted-foreground print:block'>
                            A general plan, not personal medical or coaching advice. Stop if anything causes sharp pain,
                            dizziness or chest discomfort.
                        </span>

                        <span className='mt-6 flex gap-5 rounded-xl border border-primary/30 bg-primary/5 px-4 py-4 print:hidden'>
                            <span className='min-w-0 flex-1'>
                                <span className='flex items-center gap-2 text-sm font-semibold text-foreground'>
                                    <Smartphone className='size-4 shrink-0 text-primary' />
                                    Take your plan with you
                                </span>
                                <span className='mt-1.5 block text-sm leading-relaxed text-muted-foreground'>
                                    Your plan is saved in this browser, so it is still here when you come back. To open it on
                                    your phone or another computer, copy your plan link
                                    <span className='hidden sm:inline'> or scan the code</span>. In the PDF, every exercise
                                    name is a link to how to do it.
                                </span>
                                <span className='mt-3 flex flex-wrap items-center gap-3'>
                                    <button
                                        type='button'
                                        onClick={copyPlanLink}
                                        disabled={!planLink}
                                        className='inline-flex min-h-11 items-center gap-2 rounded-full border border-primary/40 bg-background px-4 py-2 text-sm font-semibold text-foreground transition-colors hover:bg-secondary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50'
                                    >
                                        {copied ? <Check className='size-4 text-primary' /> : <Copy className='size-4' />}
                                        {copied ? 'Link copied' : 'Copy plan link'}
                                    </button>
                                    <span role='status' aria-live='polite' className='sr-only'>
                                        {copied ? 'Your plan link is copied.' : ''}
                                    </span>
                                </span>
                                <span className='mt-3 block text-xs leading-relaxed text-muted-foreground'>
                                    The link holds your answers, such as age and weight, so only share it with people you trust.
                                </span>
                            </span>
                            {planLink && (
                                <span className='hidden shrink-0 flex-col items-center gap-1.5 sm:flex'>
                                    <QrCode value={planLink} label='Scan to open this plan on your phone' className='size-28 border border-border' />
                                    <span className='text-xs text-muted-foreground'>Scan with your phone</span>
                                </span>
                            )}
                        </span>

                        <span className='mt-6 flex flex-wrap gap-3 print:hidden'>
                            <button
                                type='button'
                                onClick={printPlan}
                                className='inline-flex items-center gap-2 rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground transition-opacity hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2'
                            >
                                <Printer className='size-4' />
                                Print or save as PDF
                            </button>
                            <button
                                type='button'
                                onClick={() => {
                                    interacted.current = true
                                    setPlan(null)
                                    setStep(3)
                                }}
                                className='inline-flex items-center gap-2 rounded-full border border-border bg-background px-5 py-2.5 text-sm font-medium text-foreground transition-colors hover:bg-secondary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
                            >
                                <ArrowLeft className='size-4' />
                                Change answers
                            </button>
                            <button
                                type='button'
                                onClick={restart}
                                className='inline-flex items-center gap-2 rounded-full border border-border bg-background px-5 py-2.5 text-sm font-medium text-foreground transition-colors hover:bg-secondary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
                            >
                                <RotateCcw className='size-4' />
                                Start again
                            </button>
                        </span>
                    </div>
                )}

                <div className='mt-6 flex gap-2 rounded-xl border border-border bg-secondary/40 px-4 py-3'>
                    <Info className='mt-0.5 size-4 shrink-0 text-muted-foreground' />
                    <span className='text-xs leading-relaxed text-muted-foreground'>
                        A general plan, not personal medical or coaching advice. Stop if anything causes sharp pain,
                        dizziness or chest discomfort. Your answers are not sent to us or stored by us. A finished plan is
                        kept in this browser until you start again.
                    </span>
                </div>
            </div>
        </section>
    )
}

export default WorkoutPlanner
